import "@/assets/styles/app.css";

import * as Sentry from "@sentry/vue";
import { MutationCache, QueryCache, QueryClient, VueQueryPlugin } from "@tanstack/vue-query";
import { createApp } from "vue";

import App from "@/App.vue";
import { authPlugin, refreshSession } from "@/constants/plugins/auth";
import chartjsPlugin from "@/constants/plugins/chartjs";
import pluginFloatingVue from "@/constants/plugins/floating-vue";
import { router } from "@/router";
import { HttpError } from "@/utils";

let hasOngoingRefreshRequest = false;

const init = async () => {
	const app = createApp(App);

	Sentry.init({
		app,
		dsn: import.meta.env.VITE_SENTRY_DSN,
		environment: import.meta.env.VITE_ENVIRONMENT || "local",
		release: import.meta.env.VITE_VERSION,

		// Set tracesSampleRate to 1.0 to capture 100%
		// of transactions for tracing.
		tracesSampleRate: 1.0,

		// Profile sampled sessions while a trace is active.
		profileSessionSampleRate: 1.0,
		profileLifecycle: "trace",

		// Preserve user information collection with the Sentry 11 API.
		dataCollection: {
			userInfo: true,
			httpBodies: [],
			graphQL: { document: false, variables: false },
			genAI: { inputs: false, outputs: false },
			mcp: { inputs: false, outputs: false },
			databaseQueryData: false,
			queues: false,
			stackFrameVariables: false,
		},

		integrations: [
			Sentry.browserProfilingIntegration(),
			Sentry.vueIntegration({
				tracingOptions: {
					trackComponents: true,
				},
			}),
			Sentry.browserTracingIntegration({
				router,
			}),
		],
	});

	const queryClient = new QueryClient({
		defaultOptions: {
			queries: {
				staleTime: 60_000,
				retry: (failureCount, error) => {
					if (error instanceof HttpError && error.code === 401) {
						return false;
					}

					return failureCount < 3;
				},
			},
			mutations: {
				retry: () => {
					return false;
				},
			},
		},
		queryCache: new QueryCache({
			onError: async (error, query) => {
				if (hasOngoingRefreshRequest) {
					await new Promise((resolve) => setTimeout(resolve, 1000));
					return;
				}

				if (error instanceof HttpError && error.code === 401) {
					hasOngoingRefreshRequest = true;

					try {
						const refreshed = await refreshSession();

						if (!refreshed) {
							throw new Error("Could not refresh auth");
						}

						queryClient.invalidateQueries();

						hasOngoingRefreshRequest = false;
					} catch (error) {
						Sentry.captureException(error, {
							extra: {
								queryKey: query.queryKey,
								queryHash: query.queryHash,
							},
						});

						hasOngoingRefreshRequest = false;

						router.push({ name: "AuthLogin" });
					}
				}

				Sentry.captureException(error, {
					extra: {
						queryKey: query.queryKey,
						queryHash: query.queryHash,
					},
				});
			},
		}),
		mutationCache: new MutationCache({
			onError: async (error, variables, context, mutation) => {
				Sentry.captureException(error, {
					extra: {
						mutationId: mutation.mutationId,
						variables,
						context,
					},
				});
			},
		}),
	});

	app.use(await authPlugin());
	app.use(chartjsPlugin);
	app.use(pluginFloatingVue);
	app.use(VueQueryPlugin, {
		queryClient,
	});
	app.use(router);

	router.isReady().then(() => {
		app.mount("#app");
	});
};

init();
